import { frontOverhangViolations } from "@CommonUtils/pog/frontOverhang";
import { frontOverhangViolationText } from "./common/frontOverhangViolationText";
import html2canvas from "html2canvas";
import { atom, PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "src/i18n";
import { Color, Flex, Scroller } from "src/elements";
import styled from "styled-components";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { Pog, RelaxedItemConstraint } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useApi } from "src/api";
import { useHighlightsApi } from "src/api/merchflows/highlights";
import { Image } from "src/elements/Image/Image";
import { useUser } from "src/atoms/user";
import {
  planogramItemSearcherAtom,
  planogramKonvaRendererAtom,
  planogramNewRendererAtom,
  planogramSelectedItemsAtom,
  planogramSelectedShelvesAtom,
} from "../PlanogramExtensions/store/atoms";
import { baseHighlightsAtom, highlightsModeAtom } from "./store/atoms";
import { Bays } from "./components/Bay/Bays";
import { Containers } from "./components/Containers/Containers";
import { PlanogramPanel } from "./components/Panel/PlanogramPanel";
import { usePlanogramPanel } from "./components/Panel/store/hooks";
import { Shelves } from "./components/Shelf/Shelves";
import type { ShelfTagRule } from "./components/Shelf/MergedShelf";
import type { CanvasItemLabelRule } from "./components/Item/Item";
import { DecorativeShelves } from "./components/Shelf/DecorativeShelves";
import {
  DEFAULT_NOTCH_RULER_WIDTH,
  getBayPdfShift,
  getPdfPlanogramWidth,
  PdfNotchRulers,
} from "./components/Ruler/PdfNotchRulers";
import {
  deletedProductAtom,
  newProductAtom,
  planogramIssueBadgeClickAtom,
  planogramIssueBadgesAtom,
  planogramIssueHighlightAtom,
  planogramIssueToneAtom,
  relaxedItemsAtom,
} from "./store/atoms";
import { getOuterBounds, getSnappableFixtures, isPlanogramItemMatching } from "./store/utils";
import { usePogAtomActions } from "./store/actions";
import { useCanDeclareAisleBreaks } from "./store/aisleBreakEditing";
import { PlanogramSideView } from "./components/PlanogramSideView/PlanogramSideView";
import { planogramLegendAtom } from "../PlanogramExtensions/PlanogramLegend";
import { getHighlightColor } from "./components/Item/store/highlight";
import { getDrawnRunWidth, getSplitShifts } from "./components/KonvaPlanogram/sceneProjection";
import {
  cdtStudioHighlightAtom,
  cdtStudioPinsAtom,
  cdtStudioSelectedFlagAtom,
  cdtStudioSelectionSourceAtom,
} from "src/pages/CdtStudio/store/atoms";
import type {
  OverlayChannelInput,
  PlanogramOverlayBadgeRequest,
  PlanogramWorldOverlayState,
} from "./components/KonvaPlanogram";
import { CdtStudioPinCard } from "src/pages/CdtStudio/components/CdtStudioItemPin";
import { KonvaEditorPlanogramGate } from "./components/KonvaEditorPlanogram/KonvaEditorPlanogram";
import {
  KonvaViewerPlanogram,
  KonvaViewerPlanogramGate,
} from "./components/KonvaViewerPlanogram/KonvaViewerPlanogram";
import {
  ViewerWorldAnnotations,
  type CanvasProductCaptionRule,
} from "./components/KonvaViewerPlanogram/ViewerWorldAnnotations";
import { computeNeededSqueeze, type SqueezeRelief } from "@CommonUtils/pog/squeezeRelief";
import { DrawnSqueezeContext } from "./common/drawnSqueeze";
import { Planogram3dWithTools } from "../Planogram3dWithTools/Planogram3dWithTools";
import { refreshSelectedItem } from "./common/planogramSelection";
import type { PlanogramSelectedItem } from "./common/planogramSelection";
import type { OpenGenerate } from "src/pages/CdtStudio/components/CdtStudioPanel";

const NO_DRAWN_SQUEEZE: ReadonlyMap<object, SqueezeRelief> = new Map();

const PlanogramOuterContainer = styled(Flex)<{ $fillViewport?: boolean }>`
  position: relative;
  display: flex;
  gap: 10px;
  width: 100%;
  height: ${({ $fillViewport }) => ($fillViewport ? "100%" : undefined)};
  flex-grow: 1;
  min-height: 0;
  border-color: ${Color.primary};
  border-style: solid;
  border-radius: 3px;
  overflow: hidden;
  --shelf-color: ${Color.forest950};
`;

const PlanogramContainer = styled(Flex)<{
  isKonvaRenderer?: boolean;
  isRenderingAsImage?: boolean;
}>`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  min-width: ${({ isKonvaRenderer }) => isKonvaRenderer && "0"};
  min-height: ${({ isKonvaRenderer }) => isKonvaRenderer && "0"};
  user-select: none;
  visibility: ${({ isRenderingAsImage }) => isRenderingAsImage && "hidden"};
`;

const PlanogramElement = styled(Flex)<{
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  isKonvaRenderer?: boolean;
  pdfMode?: boolean;
}>`
  width: ${({ isKonvaRenderer, width, scaleX }) =>
    isKonvaRenderer ? "100%" : `calc(${width}px * ${scaleX})`};
  height: ${({ height, isKonvaRenderer, scaleY }) =>
    isKonvaRenderer ? "100%" : `calc(${height}px * ${scaleY})`};
  min-width: ${({ isKonvaRenderer }) => isKonvaRenderer && "0"};
  min-height: ${({ isKonvaRenderer }) => isKonvaRenderer && "0"};
  flex: ${({ isKonvaRenderer }) => isKonvaRenderer && "1 1 auto"};
  position: relative;

  ${({ pdfMode }) =>
    pdfMode &&
    `
      margin-top: 60px;
      margin-bottom: 30px;
    `}
`;

const PdfTopBox = styled(Flex)`
  position: absolute;
  flex-direction: column;
  background-color: white;
  border: 1px solid black;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: 4px;
  text-align: center;
  height: 50px;
  z-index: 10;
`;

const PdfTopBoxLine1 = styled.div`
  font-size: 14px;
  font-weight: bold;
  font-family: sans-serif;
  color: black;
`;

const PdfTopBoxLine2 = styled.div`
  font-size: 11px;
  color: black;
  font-family: sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const PdfBottomRuler = styled(Flex)`
  position: absolute;
  justify-content: center;
  align-items: center;
  height: 20px;
  box-sizing: border-box;
  z-index: 10;
`;

const PdfRulerLine = styled.div`
  position: absolute;
  left: 10px;
  right: 10px;
  height: 1px;
  background-color: black;

  &::before {
    content: "<";
    position: absolute;
    left: -10px;
    top: -9px;
    font-size: 12px;
    font-family: monospace;
    font-weight: bold;
    color: black;
  }

  &::after {
    content: ">";
    position: absolute;
    right: -10px;
    top: -9px;
    font-size: 12px;
    font-family: monospace;
    font-weight: bold;
    color: black;
  }
`;

const PdfRulerText = styled.span`
  background-color: white;
  padding: 0 4px;
  font-size: 11px;
  font-weight: bold;
  font-family: sans-serif;
  color: black;
  z-index: 1;
`;

interface Props {
  pogAtom: PrimitiveAtom<Pog | null>;
  isEditable?: boolean;
  isPog?: boolean;
  isRenderingAsImage?: boolean;
  isImageSentToConsole?: boolean;
  isPanelVisible?: boolean;
  fillViewport?: boolean;
  merchflowId?: number;
  onGenerate?: OpenGenerate;
  basePogId?: number;
  currentStoreCode?: string;
  isMetricsRecalculated?: boolean;
  setIsModified?: (modified: boolean) => void;
  isWithBorder?: boolean;
  showPanelProducts?: boolean;
  isContainersFullDetails?: boolean;
  preloadedAvailableStores?: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode?: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (item: PlanogramItem | UnrangedPlanogramItem) => string | null | undefined;
  /** Read-only viewers: on-screen label per item, drawn on the product image. */
  canvasItemLabelRule?: CanvasItemLabelRule;
  /** With `forceKonvaViewer`: the line above that label on coloured blocks. */
  canvasItemCaptionRule?: CanvasProductCaptionRule;
  /** Read-only viewers: content at the top-right of each shelf run, such as available space. */
  shelfTagRule?: ShelfTagRule;
  categoryName?: string;
  scaleX?: number;
  scaleY?: number;
  initialViewportOffsetX?: number;
  centerViewportOnFit?: boolean;
  onInspectProduct?: (pog: Pog | null) => void;
  pdfShelfLabelBayNo?: number;
  /**
   * Read-mostly viewer pages opt in to the Konva canvas renderer; the stage
   * only mounts when the `planogramKonvaRendererAtom` flag is also on.
   */
  allowKonvaViewer?: boolean;
  /**
   * Editor pages opt in to the Konva canvas renderer with the full
   * `standardPolicy` (drag, drop, context actions, container operations);
   * the stage only mounts when the `planogramKonvaRendererAtom` flag is on.
   */
  allowKonvaEditor?: boolean;
  /**
   * Read-only viewers: always mount the Konva viewer (zoom, pan), whatever the renderer flag.
   * `canvasItemLabelRule` and `shelfTagRule` are then drawn on the canvas world layer.
   */
  forceKonvaViewer?: boolean;
  /**
   * The Konva editor opens with the whole wall in view, centred with a small margin,
   * instead of fitted to its height (which leaves a long wall running off both sides).
   */
  openFitted?: boolean;
  pdfNotchRulerWidth?: number;
}

export const Planogram = (props: Props) => {
  const [pog, setPog] = useAtom(props.pogAtom);
  const [newRenderer] = useAtom(planogramNewRendererAtom);
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const setSelectedShelfIds = useSetAtom(planogramSelectedShelvesAtom);
  const { tab, setTab } = usePlanogramPanel();
  const hasResetPanelForLoadedPog = useRef(false);
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!pog) {
      hasResetPanelForLoadedPog.current = false;
      return;
    }

    if (!props.isPanelVisible || hasResetPanelForLoadedPog.current) return;

    hasResetPanelForLoadedPog.current = true;
    if (tab === "CDT_STUDIO") return; // pog load must not kick the wizard off this tab
    setTab(
      props.showPanelProducts === false
        ? "FIXTURES_DETAILS"
        : props.isPog
          ? "UNRANGED_ITEMS"
          : "UNRANGED_CONTAINERS",
    );
  }, [pog, props.isPanelVisible, props.isPog, props.showPanelProducts, tab, setTab]);

  // Effect to update selected items references after pog objects have been modified.
  // For example when you unrange items, the items are no longer on the planogram and you need to clear selected items.
  // When one item has been edited, new item object needs to be assigned as selected items.
  useEffect(() => {
    if (!pog || props.onInspectProduct) return;

    setSelectedItems((currentSelectedItems) =>
      currentSelectedItems
        .map((item) => refreshSelectedItem(pog, item))
        .filter((item): item is PlanogramSelectedItem => item !== null),
    );
    setSelectedShelfIds((currentShelfIds) =>
      currentShelfIds.filter((shelfId) => pog.getShelf(shelfId) !== null),
    );
  }, [pog, props.onInspectProduct, setSelectedItems, setSelectedShelfIds]);

  const overhangWarnings = useMemo(() => (pog ? frontOverhangViolations(pog) : []), [pog]);
  return pog ? (
    <>
      {!props.pdfMode && overhangWarnings.length > 0 && (
        <div
          role="alert"
          className="border border-amber-500 bg-amber-50 p-2 text-sm text-amber-900"
        >
          <strong>{t("frontOverhang.fixBeforeApproval")}</strong>
          {overhangWarnings.map((warning) => (
            <div key={warning.itemId}>{frontOverhangViolationText(pog, warning)}</div>
          ))}
        </div>
      )}
      {newRenderer && !props.pdfMode ? (
        <Planogram3dWithTools pog={pog} setPog={setPog} />
      ) : (
        <PlanogramInner {...props} />
      )}
    </>
  ) : null;
};

const PlanogramInner = ({
  pogAtom,
  isEditable = false,
  isPog = false,
  isRenderingAsImage,
  isImageSentToConsole,
  isPanelVisible,
  fillViewport = false,
  merchflowId,
  basePogId,
  onGenerate,
  currentStoreCode,
  setIsModified: _setIsModified,
  isWithBorder = false,
  showPanelProducts = true,
  isContainersFullDetails = false,
  preloadedAvailableStores,
  pdfMode = false,
  customItemLabelOnly,
  customLabelRule,
  canvasItemLabelRule,
  canvasItemCaptionRule,
  shelfTagRule,
  categoryName = "",
  scaleX: propsScaleX,
  scaleY: propsScaleY,
  initialViewportOffsetX,
  centerViewportOnFit,
  onInspectProduct,
  pdfShelfLabelBayNo,
  allowKonvaViewer = false,
  allowKonvaEditor = false,
  forceKonvaViewer = false,
  openFitted = false,
  pdfNotchRulerWidth,
}: Props) => {
  const { t } = useTranslation("common");
  const { getMerchflowReviewApi } = useApi();
  const { deleteItem } = usePogAtomActions(pogAtom);
  const { getBaseHighlightsApi } = useHighlightsApi();
  const { user } = useUser();

  const [pog, setPog] = useAtom(pogAtom);
  // A store's fixtures always edit on the canvas: it is the only renderer that draws an aisle
  // break, and holds the seam handle that sets one.
  const canDeclareAisleBreaks = useCanDeclareAisleBreaks();
  const konvaRendererEnabled = useAtomValue(planogramKonvaRendererAtom) || canDeclareAisleBreaks;
  const [search] = useAtom(planogramItemSearcherAtom);
  const highlightsMode = useAtomValue(highlightsModeAtom);
  const baseHighlights = useAtomValue(baseHighlightsAtom);
  const planogramLegend = useAtomValue(planogramLegendAtom);
  const [deletedProduct, setDeletedProduct] = useAtom(deletedProductAtom);
  const [newProduct, setNewProduct] = useAtom(newProductAtom);
  const setRelaxedItems = useSetAtom(relaxedItemsAtom);

  const nonNullPogAtom: PrimitiveAtom<Pog> = useMemo(() => {
    return atom(
      (get) => {
        const val = get(pogAtom);
        if (!val) {
          throw new Error("Pog cannot be null");
        }
        return val;
      },
      (get, set, update) => {
        set(pogAtom, update);
      },
    );
  }, [pogAtom]);

  const bayCdt1Strings = useMemo(() => {
    if (!pog || !pdfMode) return {};
    const maps: Record<string, string> = {};
    for (const bay of pog.planogram.bays) {
      const cdt1Set = new Set<string>();
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (item.cdt1) {
            cdt1Set.add(item.cdt1);
          }
        }
      }
      for (const pegboard of bay.pegboards) {
        for (const placement of pegboard.placements) {
          if (placement.pegProduct?.cdt1) {
            cdt1Set.add(placement.pegProduct.cdt1);
          }
        }
      }
      maps[bay.uniqueId] = Array.from(cdt1Set).join(", ");
    }
    return maps;
  }, [pog, pdfMode]);

  const [renderedImageData, setRenderedImageData] = useState<string | null>(null);
  const [scaleXState, setScaleXState] = useState<number | null>(null);
  const [scaleYState, setScaleYState] = useState<number | null>(null);

  const scaleX = propsScaleX !== undefined ? propsScaleX : scaleXState;
  const scaleY = propsScaleY !== undefined ? propsScaleY : scaleYState;

  const setScaleX = setScaleXState;
  const setScaleY = setScaleYState;

  const [availableStores, setAvailableStores] = useState<
    ResponseMerchFlowApplyFilters["filter_config"] | null
  >(preloadedAvailableStores ?? null);

  const refOuterContainer = useRef<HTMLDivElement>(null);
  const refViewport = useRef<HTMLDivElement>(null);
  const refContainer = useRef<HTMLDivElement>(null);
  const refScaleCalculator = useRef<number | null>(null);

  const loadStores = async (merchflowId: number) => {
    const response = await getMerchflowReviewApi(merchflowId);
    if (response) {
      setAvailableStores(response.filter_config);
    }
  };

  const triggerRenderingAsImage = () => {
    if (refContainer.current) {
      html2canvas(refContainer.current, {
        allowTaint: true,
        useCORS: true,
        backgroundColor: Color.sand50,
        onclone(_, ele) {
          ele.style.visibility = "visible";
        },
      }).then((canvas) => {
        const image = canvas.toDataURL("image/png", 1.0);
        setRenderedImageData(image);

        if (isImageSentToConsole) {
          // Do not remove this console: the thumbnail service reads the image from page logs.
          console.log(image);
        }
      });
    }
  };

  useEffect(() => {
    if (preloadedAvailableStores) {
      setAvailableStores(preloadedAvailableStores);
      return;
    }
    if (!preloadedAvailableStores && merchflowId) {
      void loadStores(merchflowId);
    }
  }, [merchflowId, preloadedAvailableStores]);

  useEffect(() => {
    if (isRenderingAsImage && !renderedImageData && scaleX && scaleY) {
      triggerRenderingAsImage();
    }
  }, [isRenderingAsImage, renderedImageData, scaleX, scaleY]);

  useEffect(() => {
    if (deletedProduct) {
      deleteItem(deletedProduct.item, deletedProduct.storeCodes);
      setDeletedProduct(null);
    }
  }, [deletedProduct]);

  useEffect(() => {
    if (newProduct) {
      setPog((pog) => {
        if (!pog) return null;

        const pogNonCircular = pog.toNonCircular();

        pogNonCircular.unrangedItems.splice(0, 0, {
          ...newProduct,
          shelf: null,
          potentialShelves: [],
          reasons: [],
        });

        return new Pog(pogNonCircular);
      });

      setNewProduct(null);
    }
  }, [newProduct]);

  const highlightedItems = useMemo(() => {
    if (!pog || search === "") {
      return [];
    }

    const highlightedItems: string[] = [];

    for (const bay of pog.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (isPlanogramItemMatching(item, search)) {
            highlightedItems.push(item.uniqueId);
          }
        }
      }

      for (const pegboard of bay.pegboards) {
        for (const placement of pegboard.placements) {
          if (isPlanogramItemMatching(placement.pegProduct, search)) {
            highlightedItems.push(placement.uniqueId);
          }
        }
      }
    }

    return highlightedItems;
  }, [pog, search]);

  const cdtStudioHighlights = useAtomValue(cdtStudioHighlightAtom);
  const issueHighlights = useAtomValue(planogramIssueHighlightAtom);
  const issueBadges = useAtomValue(planogramIssueBadgesAtom);
  const issueTone = useAtomValue(planogramIssueToneAtom);
  const setIssueBadgeClick = useSetAtom(planogramIssueBadgeClickAtom);

  const combinedHighlightedItems = useMemo(
    () =>
      cdtStudioHighlights.length > 0 || issueHighlights.length > 0
        ? [...highlightedItems, ...cdtStudioHighlights, ...issueHighlights]
        : highlightedItems,
    [highlightedItems, cdtStudioHighlights, issueHighlights],
  );

  const overlayChannels = useMemo<readonly OverlayChannelInput[]>(() => {
    const channels: OverlayChannelInput[] = [];
    if (highlightedItems.length > 0) {
      channels.push({
        ids: highlightedItems,
        mode: "highlight",
        name: "search",
        priority: 100,
        pulse: true,
      });
    }
    if (cdtStudioHighlights.length > 0) {
      channels.push({
        ids: cdtStudioHighlights,
        mode: "highlight",
        name: "cdt-studio",
        priority: 9,
      });
    }
    if (issueHighlights.length > 0) {
      channels.push({
        ids: issueHighlights,
        mode: "highlight",
        name: "review-issue",
        priority: 30,
        ...(issueTone
          ? {
              itemColors: new Map(issueHighlights.map((id) => [id, issueTone.color])),
              outline: issueTone.outline,
              badgeColor: issueTone.color,
            }
          : {}),
      });
    }

    if (highlightsMode && baseHighlights && pog) {
      const businessColors = new Map<string, string>();
      const enabledHighlights = baseHighlights.filter((highlight) => highlight.enabled);
      const resolveBusinessColor = (product: Parameters<typeof getHighlightColor>[1]) => {
        for (const highlight of enabledHighlights) {
          const highlightColor = getHighlightColor(highlight, product, pog, planogramLegend);
          if (highlightColor) return highlightColor;
        }
        return false;
      };
      for (const item of pog.planogram.getAllItems()) {
        const businessColor = resolveBusinessColor(item);
        if (businessColor) businessColors.set(item.uniqueId, businessColor);
      }
      for (const placement of pog.planogram.getAllPegboardPlacements()) {
        const businessColor = resolveBusinessColor(placement.pegProduct);
        if (businessColor) businessColors.set(placement.uniqueId, businessColor);
      }
      if (businessColors.size > 0) {
        channels.push({
          ids: [...businessColors.keys()],
          itemColors: businessColors,
          mode: "highlight",
          name: "business",
          priority: 20,
        });
      }
    }

    return channels;
  }, [
    baseHighlights,
    cdtStudioHighlights,
    highlightedItems,
    highlightsMode,
    issueHighlights,
    issueTone,
    planogramLegend,
    pog,
  ]);

  const cdtStudioPins = useAtomValue(cdtStudioPinsAtom);
  const [selectedCdtFlag, setSelectedCdtFlag] = useAtom(cdtStudioSelectedFlagAtom);
  const [cdtSelectionSource, setCdtSelectionSource] = useAtom(cdtStudioSelectionSourceAtom);
  const [canvasPinCard, setCanvasPinCard] = useState<{
    flagId: string;
    number: number;
    position: { left: number; top: number };
  } | null>(null);
  useEffect(() => {
    if (selectedCdtFlag === null) setCanvasPinCard(null);
  }, [selectedCdtFlag]);
  const overlayBadges = useMemo(() => {
    const entries = Object.entries(cdtStudioPins);
    const issueEntries = Object.entries(issueBadges);
    if (entries.length === 0 && issueEntries.length === 0) return undefined;

    return new Map([
      ...issueEntries,
      ...entries.map(([uniqueId, pin]) => [uniqueId, String(pin.number)] as const),
    ]);
  }, [cdtStudioPins, issueBadges]);
  const activateOverlayBadge = useCallback(
    ({ entityId, position }: PlanogramOverlayBadgeRequest) => {
      const issueBadge = issueBadges[entityId];
      if (issueBadge !== undefined) {
        setIssueBadgeClick({
          entityId,
          badge: issueBadge,
          clientX: position.clientX,
          clientY: position.clientY,
          at: Date.now(),
        });
        return;
      }
      const pin = cdtStudioPins[entityId];
      if (!pin) return;
      setCdtSelectionSource("pin");
      setSelectedCdtFlag(pin.flagId);
      setCanvasPinCard({
        flagId: pin.flagId,
        number: pin.number,
        position: {
          left: Math.min(Math.max(position.clientX + 8, 8), window.innerWidth - 316),
          top: Math.min(Math.max(position.clientY - 8, 8), window.innerHeight - 240),
        },
      });
    },
    [cdtStudioPins, issueBadges, setCdtSelectionSource, setIssueBadgeClick, setSelectedCdtFlag],
  );

  const searchScrollTarget = highlightedItems.length === 1 ? (highlightedItems[0] ?? null) : null;
  const scrollToEntityId =
    cdtSelectionSource === "panel"
      ? (cdtStudioHighlights[0] ?? searchScrollTarget)
      : cdtSelectionSource === "pin"
        ? null
        : searchScrollTarget;

  const calculateScales = useCallback(() => {
    if (propsScaleX !== undefined && propsScaleY !== undefined) {
      return;
    }
    const target = refViewport.current ?? refContainer.current ?? refOuterContainer.current;
    if (!target || !pog) {
      return;
    }

    const planogramWidth = getDrawnRunWidth(pog.planogram);
    const planogramHeight = pog.planogram.getMaxHeight();
    const { width: containerWidth, height: containerHeight } = target.getBoundingClientRect();

    if (
      !Number.isFinite(planogramWidth) ||
      !Number.isFinite(planogramHeight) ||
      planogramWidth <= 0 ||
      planogramHeight <= 0 ||
      containerWidth <= 0 ||
      containerHeight <= 0
    ) {
      return;
    }

    const nextScaleY = (containerHeight - 6) / planogramHeight;
    const nextScaleX = containerWidth / planogramWidth;
    const minScale = Math.min(nextScaleX, nextScaleY);
    const resolvedScaleY = (isRenderingAsImage && minScale) || nextScaleY;
    const resolvedScaleX = (isRenderingAsImage && minScale) || (isPog && nextScaleY) || nextScaleX;

    setScaleY((currentScaleY) =>
      currentScaleY !== null && Math.abs(currentScaleY - resolvedScaleY) < 0.0001
        ? currentScaleY
        : resolvedScaleY,
    );
    setScaleX((currentScaleX) =>
      currentScaleX !== null && Math.abs(currentScaleX - resolvedScaleX) < 0.0001
        ? currentScaleX
        : resolvedScaleX,
    );
  }, [isPog, isRenderingAsImage, pog, propsScaleX, propsScaleY]);

  useEffect(() => {
    if (propsScaleX !== undefined && propsScaleY !== undefined) {
      return;
    }
    const scaleViewport = refOuterContainer.current;
    const measuredViewport = refViewport.current ?? refContainer.current;

    if (!scaleViewport && !measuredViewport) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (refScaleCalculator.current !== null) {
        window.clearTimeout(refScaleCalculator.current);
      }

      refScaleCalculator.current = window.setTimeout(() => {
        calculateScales();
      }, 100);
    });

    if (scaleViewport) {
      resizeObserver.observe(scaleViewport);
    }

    if (measuredViewport && measuredViewport !== scaleViewport) {
      resizeObserver.observe(measuredViewport);
    }

    calculateScales();

    return () => {
      resizeObserver.disconnect();

      if (refScaleCalculator.current !== null) {
        window.clearTimeout(refScaleCalculator.current);
        refScaleCalculator.current = null;
      }
    };
  }, [calculateScales, propsScaleX, propsScaleY]);

  // Load base highlights.
  useEffect(() => {
    if (merchflowId) {
      getBaseHighlightsApi(merchflowId);
    }
  }, [merchflowId]);

  useEffect(() => {
    setRelaxedItems(
      pog?.relaxedConstraints.filter((item): item is RelaxedItemConstraint =>
        Boolean(typeof item.entity === "object" && "itemId" in item.entity),
      ) || null,
    );
  }, []);

  const setIsModified = () => {
    if (_setIsModified) {
      _setIsModified(true);
    }
  };

  const notchRulerWidth = pdfNotchRulerWidth ?? DEFAULT_NOTCH_RULER_WIDTH;
  const splitShifts = pog ? getSplitShifts(pog.planogram.bays) : [];
  const isKonvaRenderer =
    !pdfMode &&
    !isRenderingAsImage &&
    (forceKonvaViewer || (konvaRendererEnabled && (allowKonvaEditor || allowKonvaViewer)));
  // The canvas works the squeeze out as it projects; only the DOM renderer needs it here.
  const drawnSqueeze = useMemo(
    () => (pog && !isKonvaRenderer ? computeNeededSqueeze(pog.planogram) : NO_DRAWN_SQUEEZE),
    [pog, isKonvaRenderer],
  );
  const planogramElementWidth = pog
    ? pdfMode
      ? getPdfPlanogramWidth(
          getDrawnRunWidth(pog.planogram),
          pog.planogram.bays.length,
          notchRulerWidth,
        )
      : getDrawnRunWidth(pog.planogram)
    : 0;

  // Element, not an inner component: a new component type remounts the whole tree every render.
  const planogramDomSubtree =
    pog && scaleX && scaleY ? (
      <DrawnSqueezeContext.Provider value={drawnSqueeze}>
        <Bays
          bays={pog.planogram.bays}
          scaleX={scaleX}
          scaleY={scaleY}
          isEditable={isEditable}
          highlightedItems={combinedHighlightedItems}
          pogAtom={nonNullPogAtom}
          pdfMode={pdfMode}
          pdfNotchRulerWidth={pdfNotchRulerWidth}
        />

        <Shelves
          currentStoreCode={currentStoreCode}
          scaleX={scaleX}
          scaleY={scaleY}
          isEditable={isEditable}
          isPog={isPog}
          highlightedItems={combinedHighlightedItems}
          pogAtom={nonNullPogAtom}
          setIsModified={setIsModified}
          availableStores={availableStores}
          pdfMode={pdfMode}
          customItemLabelOnly={customItemLabelOnly}
          customLabelRule={customLabelRule}
          canvasItemLabelRule={canvasItemLabelRule}
          shelfTagRule={shelfTagRule}
          disableMergedShelves={pdfMode}
          pdfShelfLabelBayNo={pdfShelfLabelBayNo}
          pdfNotchRulerWidth={pdfNotchRulerWidth}
        />

        {!isPog && (
          <Containers
            scaleX={scaleX}
            scaleY={scaleY}
            bounds={getOuterBounds(pog.planogram)}
            snappables={getSnappableFixtures(pog.planogram)}
            isEditable={isEditable}
            isContainersFullDetails={isContainersFullDetails}
            pogAtom={nonNullPogAtom}
          />
        )}
      </DrawnSqueezeContext.Provider>
    ) : null;

  const renderDecorativeShelves = (
    shelfDragPreview: PlanogramWorldOverlayState["shelfDragPreview"] = null,
  ) =>
    pog && scaleX && scaleY ? (
      <DecorativeShelves
        bays={pog.planogram.bays}
        scaleX={isKonvaRenderer ? 1 : scaleX}
        scaleY={isKonvaRenderer ? 1 : scaleY}
        merchantName={user?.merchantName}
        categoryName={categoryName}
        pdfMode={pdfMode}
        pdfShelfLabelBayNo={pdfShelfLabelBayNo}
        pdfNotchRulerWidth={pdfNotchRulerWidth}
        shelfDragPreview={shelfDragPreview}
      />
    ) : null;
  const decorativeShelves = renderDecorativeShelves();
  const decorativeShelvesOverlay =
    pog && scaleX && scaleY
      ? ({ shelfDragPreview }: PlanogramWorldOverlayState) =>
          renderDecorativeShelves(shelfDragPreview)
      : null;
  const viewerWorldOverlay = pog ? (
    <>
      {renderDecorativeShelves()}
      <ViewerWorldAnnotations
        pog={pog}
        canvasItemLabelRule={canvasItemLabelRule}
        canvasItemCaptionRule={canvasItemCaptionRule}
        shelfTagRule={shelfTagRule}
      />
    </>
  ) : null;

  const planogramRenderer = (
    <PlanogramContainer
      isKonvaRenderer={isKonvaRenderer}
      ref={refContainer}
      isRenderingAsImage={isRenderingAsImage}
    >
      {pog && scaleX && scaleY && (
        <PlanogramElement
          width={planogramElementWidth}
          height={pog.planogram.getMaxHeight()}
          isKonvaRenderer={isKonvaRenderer}
          scaleX={scaleX}
          scaleY={scaleY}
          pdfMode={pdfMode}
        >
          {pdfMode && scaleX !== null && scaleY !== null && (
            <PdfNotchRulers
              bays={pog.planogram.bays}
              scaleX={scaleX}
              scaleY={scaleY}
              rulerWidth={pdfNotchRulerWidth}
            />
          )}

          {pdfMode &&
            scaleX !== null &&
            scaleY !== null &&
            pog.planogram.bays.map((bay, bayIndex) => {
              const bayShift = getBayPdfShift(bayIndex, notchRulerWidth) + splitShifts[bayIndex];
              const xLeft = bay.getXLeft() + bayShift;
              const width = bay.width;

              const cdt1String = bayCdt1Strings[bay.uniqueId] || "";
              const formatWidth = (w: number) => {
                const widthInCm = w / 100;
                const meters = Math.floor(widthInCm / 100);
                const cm = Number((widthInCm % 100).toFixed(1));
                return t("widthFormatted", { value: meters, cm });
              };

              return (
                <Fragment key={bay.uniqueId}>
                  <PdfTopBox
                    style={{
                      left: `${xLeft * scaleX}px`,
                      width: `${width * scaleX}px`,
                      top: "-55px",
                    }}
                  >
                    <PdfTopBoxLine1>
                      {categoryName} {pdfShelfLabelBayNo ?? bay.getNo()}
                    </PdfTopBoxLine1>
                    <PdfTopBoxLine2 title={cdt1String}>{cdt1String}</PdfTopBoxLine2>
                  </PdfTopBox>

                  <PdfBottomRuler
                    style={{
                      left: `${xLeft * scaleX}px`,
                      width: `${width * scaleX}px`,
                      bottom: "-25px",
                    }}
                  >
                    <PdfRulerLine />
                    <PdfRulerText>{formatWidth(width)}</PdfRulerText>
                  </PdfBottomRuler>
                </Fragment>
              );
            })}

          {pdfMode || isRenderingAsImage ? (
            planogramDomSubtree
          ) : forceKonvaViewer ? (
            <KonvaViewerPlanogram
              onOverlayBadgeActivate={activateOverlayBadge}
              overlayBadges={overlayBadges}
              overlayChannels={overlayChannels}
              worldOverlay={viewerWorldOverlay}
              pog={pog}
              scaleY={scaleY}
              scrollToEntityId={scrollToEntityId}
              hideProductLabels={Boolean(canvasItemLabelRule || canvasItemCaptionRule)}
            />
          ) : allowKonvaEditor ? (
            <KonvaEditorPlanogramGate
              centerOnFit={centerViewportOnFit}
              initialOffsetX={initialViewportOffsetX}
              availableStores={availableStores}
              isEditable={isEditable}
              onOverlayBadgeActivate={activateOverlayBadge}
              overlayBadges={overlayBadges}
              overlayChannels={overlayChannels}
              worldOverlay={decorativeShelvesOverlay}
              pogAtom={nonNullPogAtom}
              scaleY={scaleY}
              scrollToEntityId={scrollToEntityId}
              setIsModified={setIsModified}
              openFitted={openFitted}
            >
              {planogramDomSubtree}
            </KonvaEditorPlanogramGate>
          ) : allowKonvaViewer ? (
            <KonvaViewerPlanogramGate
              centerOnFit={centerViewportOnFit}
              onInspectProduct={onInspectProduct}
              initialOffsetX={initialViewportOffsetX}
              onOverlayBadgeActivate={activateOverlayBadge}
              overlayBadges={overlayBadges}
              overlayChannels={overlayChannels}
              worldOverlay={decorativeShelvesOverlay}
              pog={pog}
              scaleY={scaleY}
              scrollToEntityId={scrollToEntityId}
            >
              {planogramDomSubtree}
            </KonvaViewerPlanogramGate>
          ) : (
            planogramDomSubtree
          )}
          {!isKonvaRenderer && decorativeShelves}
        </PlanogramElement>
      )}
      {canvasPinCard && selectedCdtFlag === canvasPinCard.flagId && (
        <CdtStudioPinCard
          flagId={canvasPinCard.flagId}
          number={canvasPinCard.number}
          onClose={() => {
            setSelectedCdtFlag(null);
            setCdtSelectionSource(null);
            setCanvasPinCard(null);
          }}
          position={canvasPinCard.position}
        />
      )}
    </PlanogramContainer>
  );

  return (
    <PlanogramOuterContainer
      ref={refOuterContainer}
      borderWidth={isWithBorder ? "1px" : "0"}
      $fillViewport={centerViewportOnFit || fillViewport}
      data-testid="planogram-layout"
    >
      <Flex
        width="100%"
        height={fillViewport ? "100%" : undefined}
        flexGrow={1}
        minHeight="0"
        minWidth="0"
      >
        {isRenderingAsImage && !renderedImageData && planogramRenderer}

        {!isRenderingAsImage && (
          <>
            <PlanogramSideView pogAtom={pogAtom} scaleX={scaleX ?? 1} scaleY={scaleY ?? 1} />

            <Flex ref={refViewport} flexGrow={1} minHeight="0" minWidth="0">
              <Scroller gutter="auto">{planogramRenderer}</Scroller>
            </Flex>

            {pog && isPanelVisible && (
              <PlanogramPanel
                fillViewport={fillViewport}
                isEditable={isEditable}
                isPog={isPog}
                scaleX={scaleX || 0}
                scaleY={scaleY || 0}
                pogAtom={nonNullPogAtom}
                merchflowId={merchflowId}
                currentStoreCode={currentStoreCode}
                availableStores={availableStores}
                showProducts={showPanelProducts}
                onGenerate={onGenerate}
                basePogId={basePogId}
              />
            )}
          </>
        )}

        {renderedImageData && <Image src={renderedImageData} />}
      </Flex>
    </PlanogramOuterContainer>
  );
};
